<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useBranding } from 'shared/composables/useBranding';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NewWebhook from './NewWebHook.vue';
import EditWebhook from './EditWebHook.vue';
import WebhookRow from './WebhookRow.vue';
import BaseSettingsHeader from '../../components/BaseSettingsHeader.vue';
import SettingsLayout from '../../SettingsLayout.vue';
import aiPromptAPI from 'dashboard/api/aiPrompt';

export default {
  components: {
    SettingsLayout,
    NextButton,
    BaseSettingsHeader,
    NewWebhook,
    EditWebhook,
    WebhookRow,
  },
  setup() {
    const { replaceInstallationName } = useBranding();
    return { replaceInstallationName };
  },
  data() {
    return {
      loading: {},
      showAddPopup: false,
      showEditPopup: false,
      showDeleteConfirmationPopup: false,
      showAiPromptPopup: false,
      selectedWebHook: {},
      // AI Prompt
      aiSaving: false,
      systemPrompt: '',
    };
  },
  computed: {
    ...mapGetters({
      records: 'webhooks/getWebhooks',
      uiFlags: 'webhooks/getUIFlags',
    }),
    integration() {
      return this.$store.getters['integrations/getIntegration']('webhook');
    },
    tableHeaders() {
      return [
        this.$t(
          'INTEGRATION_SETTINGS.WEBHOOK.LIST.TABLE_HEADER.WEBHOOK_ENDPOINT'
        ),
        this.$t('INTEGRATION_SETTINGS.WEBHOOK.LIST.TABLE_HEADER.ACTIONS'),
      ];
    },
  },
  mounted() {
    this.$store.dispatch('webhooks/get');
  },
  methods: {
    openAddPopup() {
      this.showAddPopup = true;
    },
    hideAddPopup() {
      this.showAddPopup = false;
    },
    openDeletePopup(response) {
      this.showDeleteConfirmationPopup = true;
      this.selectedWebHook = response;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },
    openEditPopup(webhook) {
      this.showEditPopup = true;
      this.selectedWebHook = webhook;
    },
    hideEditPopup() {
      this.showEditPopup = false;
    },
    openAiPromptPopup() {
      this.showAiPromptPopup = true;
      this.fetchPrompt();
    },
    hideAiPromptPopup() {
      this.showAiPromptPopup = false;
    },
    confirmDeletion() {
      this.loading[this.selectedWebHook.id] = true;
      this.closeDeletePopup();
      this.deleteWebhook(this.selectedWebHook.id);
    },
    async deleteWebhook(id) {
      try {
        await this.$store.dispatch('webhooks/delete', id);
        useAlert(
          this.$t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.API.SUCCESS_MESSAGE')
        );
      } catch (error) {
        useAlert(
          this.$t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.API.ERROR_MESSAGE')
        );
      }
    },
    async toggleWebhook(webhook) {
      try {
        await this.$store.dispatch('webhooks/update', {
          id: webhook.id,
          webhook: { active: !webhook.active },
        });
        useAlert(
          this.$t(
            webhook.active
              ? 'INTEGRATION_SETTINGS.WEBHOOK.TOGGLE.DISABLE_SUCCESS'
              : 'INTEGRATION_SETTINGS.WEBHOOK.TOGGLE.ENABLE_SUCCESS'
          )
        );
      } catch (error) {
        useAlert(
          this.$t('INTEGRATION_SETTINGS.WEBHOOK.TOGGLE.ERROR')
        );
      }
    },
    // AI Prompt methods
    async fetchPrompt() {
      this.aiLoading = true;
      try {
        const response = await aiPromptAPI.show();
        this.systemPrompt = response.data.system_prompt || '';
        this.aiEnabled = response.data.ai_enabled || false;
      } catch (error) {
        // silently fail - prompt section will just show defaults
      } finally {
        this.aiLoading = false;
      }
    },
    async savePrompt() {
      this.aiSaving = true;
      try {
        await aiPromptAPI.update({
          system_prompt: this.systemPrompt,
          ai_enabled: this.aiEnabled,
        });
        useAlert('Treinamento da IA salvo com sucesso!');
      } catch (error) {
        useAlert('Erro ao salvar o treinamento da IA.');
      } finally {
        this.aiSaving = false;
      }
    },
  },
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.fetchingList"
    :loading-message="$t('INTEGRATION_SETTINGS.WEBHOOK.LOADING')"
    :no-records-message="$t('INTEGRATION_SETTINGS.WEBHOOK.LIST.404')"
    :no-records-found="!records.length"
  >
    <template #header>
      <BaseSettingsHeader
        v-if="integration.name"
        :title="integration.name"
        :description="replaceInstallationName(integration.description)"
        :link-text="$t('INTEGRATION_SETTINGS.WEBHOOK.LEARN_MORE')"
        feature-name="webhook"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      >
        <template #actions>
          <NextButton
            blue
            icon="i-lucide-circle-plus"
            :label="$t('INTEGRATION_SETTINGS.WEBHOOK.HEADER_BTN_TXT')"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full divide-y divide-n-weak">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 ltr:pr-4 rtl:pl-4 text-left font-semibold text-n-slate-11 last:text-right last:pr-4"
          >
            {{ thHeader }}
          </th>
        </thead>
        <tbody class="divide-y divide-n-weak flex-1 text-n-slate-12">
          <WebhookRow
            v-for="(webHookItem, index) in records"
            :key="webHookItem.id"
            :index="index"
            :webhook="webHookItem"
            @edit="openEditPopup"
            @delete="openDeletePopup"
            @toggle="toggleWebhook"
            @ai-training="openAiPromptPopup"
          />
        </tbody>
      </table>
    </template>
    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <NewWebhook v-if="showAddPopup" :on-close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditWebhook
        v-if="showEditPopup"
        :id="selectedWebHook.id"
        :value="selectedWebHook"
        :on-close="hideEditPopup"
      />
    </woot-modal>
    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.TITLE')"
      :message="
        $t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.MESSAGE', {
          webhookURL: selectedWebHook.url,
        })
      "
      :confirm-text="$t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.YES')"
      :reject-text="$t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.CONFIRM.NO')"
    />

    <!-- AI Prompt Modal -->
    <woot-modal v-model:show="showAiPromptPopup" :on-close="hideAiPromptPopup">
      <div class="flex flex-col h-auto overflow-auto p-8" style="max-width: 720px;">
        <h2 class="text-xl font-bold text-n-slate-12 mb-2">
          Treinamento da IA
        </h2>
        <p class="text-sm text-n-slate-11 mb-6">
          Configure o prompt de sistema que define a personalidade e comportamento do atendente virtual.
        </p>

        <!-- Prompt Editor -->
        <div class="flex flex-col gap-2 mb-4">
          <label class="font-semibold text-n-slate-12">
            Prompt de Sistema
          </label>
          <p class="text-sm text-n-slate-11 mb-2">
            Este texto define como a IA se comporta. Escreva instruções sobre tom de voz,
            produtos, regras de atendimento, restrições, etc.
          </p>
          <textarea
            v-model="systemPrompt"
            class="prompt-textarea"
            rows="16"
            placeholder="Exemplo: Você é um assistente virtual da empresa X, especializado em..."
          />
        </div>

        <div class="flex justify-end gap-2">
          <NextButton
            slate
            faded
            label="Cancelar"
            @click="hideAiPromptPopup"
          />
          <NextButton
            blue
            :label="aiSaving ? 'Salvando...' : 'Salvar Treinamento'"
            :disabled="aiSaving"
            @click="savePrompt"
          />
        </div>
      </div>
    </woot-modal>
  </SettingsLayout>
</template>

<style scoped>
.prompt-textarea {
  width: 100%;
  min-height: 300px;
  padding: 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 14px;
  line-height: 1.6;
  resize: vertical;
  background-color: #fafafa;
  color: #1e293b;
  transition: border-color 0.2s ease;
}
.prompt-textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}
</style>
